import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Text} from '@dropbox/dig-components/typography';
import type {WorkstreamContinueUpdatingCandidate} from 'client';
import {StatusButton} from 'components/DSYS/StatusButton';
import {t} from 'i18next';

import styles from './ContinueUpdatingModal.module.css';
import {getWorkstreamUpdateVisualStatus} from './utils';

type ContinueUpdatingModalProps = {
  candidates: WorkstreamContinueUpdatingCandidate[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onClose: () => void;
  onSelectCandidate: (candidate: WorkstreamContinueUpdatingCandidate) => void;
  onViewMore?: () => void;
  open: boolean;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: '2-digit',
});

const parseApiDateTime = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return new Date(`${value}Z`);
  }

  return new Date(value);
};

export const formatContinueUpdatingDate = (value: string) => {
  const date = parseApiDateTime(value);

  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return dateFormatter.format(date);
};

const getStatusValue = (status?: string | null) => status || 'no_status';

const ContinueUpdatingRow = ({
  candidate,
  onSelectCandidate,
}: {
  candidate: WorkstreamContinueUpdatingCandidate;
  onSelectCandidate: (candidate: WorkstreamContinueUpdatingCandidate) => void;
}) => {
  const status = getWorkstreamUpdateVisualStatus(getStatusValue(candidate.latest_update_status));

  return (
    <div className={styles.row}>
      <button
        aria-label={t('continue_updating_modal_row_aria_label', {name: candidate.name})}
        className={styles.rowMain}
        onClick={() => onSelectCandidate(candidate)}
        type="button"
      >
        <span className={styles.itemText}>
          <Text isBold className={styles.title}>
            {candidate.name}
          </Text>
          <span className={styles.metaLine}>
            <Text size="small" color="subtle">
              {candidate.latest_update_author.name}
            </Text>
            <span className={styles.metaDivider} />
            <Text size="small" color="subtle">
              {t('last_updated')}
            </Text>
            <Text size="small" color="subtle">
              {formatContinueUpdatingDate(candidate.latest_update_at)}
            </Text>
          </span>
        </span>
      </button>
      <span className={styles.status}>
        <StatusButton
          status={status}
          onClick={() => onSelectCandidate(candidate)}
          includeArchived
          size="small"
        />
      </span>
    </div>
  );
};

export const ContinueUpdatingModal = ({
  candidates,
  hasNextPage = false,
  isFetchingNextPage = false,
  onClose,
  onSelectCandidate,
  onViewMore,
  open,
}: ContinueUpdatingModalProps) => {
  if (!open || candidates.length === 0) {
    return null;
  }

  return (
    <Modal isCentered open withCloseButton="Close" onRequestClose={onClose} width={760}>
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title weightVariant="emphasized">{t('continue_updating_modal_title')}</Modal.Title>
        <Text color="subtle" className={styles.subtitle}>
          {t('continue_updating_modal_subtitle')}
        </Text>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <div className={styles.list}>
          {candidates.map((candidate) => (
            <ContinueUpdatingRow
              key={candidate.id}
              candidate={candidate}
              onSelectCandidate={onSelectCandidate}
            />
          ))}
        </div>
        {hasNextPage && onViewMore ? (
          <div className={styles.footer}>
            <Button
              variant="transparent"
              size="small"
              isLoading={isFetchingNextPage}
              disabled={isFetchingNextPage}
              onClick={onViewMore}
            >
              {t('view_more')}
            </Button>
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};
